import { memo, PropsWithChildren } from 'react';
import clsx from 'clsx';
import { ComponentMemo } from '@headless-workspace/typings';
import { GlowCTA } from '../molecules/cta/GlowCTA';
import { PropsWithAriaLabel, PropsWithClassName, PropsWithDataLayer, PropsWithTestId } from '../props';

type ContainerProps = PropsWithChildren & PropsWithClassName & PropsWithDataLayer & PropsWithTestId;

/**
 * Container component that wraps its children with additional data layer attributes.
 *
 * @param {ContainerProps} props - The properties for the Container component.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the container.
 * @param {string} [props.className] - Additional class names to apply to the container.
 * @param {object} props.dataLayer - Data layer object for tracking purposes.
 */
const Root: ComponentMemo<ContainerProps> = memo(({ children, className, dataLayer, dataTestId }: ContainerProps) => {
    return (
        <div
            className={clsx(className, dataLayer?.className)}
            data-layer-banner-action={dataLayer?.action}
            data-layer-id={dataLayer?.id}
            data-layer-name={dataLayer?.name}
            data-testid={dataTestId}
        >
            {children}
        </div>
    );
});

type WrapperProps = PropsWithClassName &
    PropsWithChildren &
    PropsWithAriaLabel & {
        href?: string;
    };

/**
 * Wrapper component that wraps its children with additional data layer attributes.
 *
 * @param {WrapperProps} props - The properties for the Wrapper component.
 * @param {React.ReactNode} props.children - The child elements to be rendered inside the wrapper.
 * @param {string} [props.className] - Additional class names to apply to the wrapper.
 * @param {string} [props.href] - The URL to navigate to when the wrapper is clicked.
 * @param {string} [props.ariaLabel] - The aria label for the wrapper.
 */
const Wrapper: ComponentMemo<WrapperProps> = memo(({ className, children, href, ariaLabel }: WrapperProps) => {
    if (href) {
        return (
            <GlowCTA href={href} ariaLabel={ariaLabel} className={className}>
                {children}
            </GlowCTA>
        );
    }

    if (className) {
        return (
            <div className={className} aria-label={ariaLabel}>
                {children}
            </div>
        );
    }

    return children;
});

export const ComposableContainer = {
    Root,
    Wrapper,
};

ComposableContainer.Root.displayName = 'ComposableContainerRoot';
ComposableContainer.Wrapper.displayName = 'ComposableContainerWrapper';
